import { Banner, IconButton, Text } from '@dixa/lib-shared-habla';
import { useContext } from 'react';

import { appActions } from 'app/app.actions';
import { KeyCombinationRecorderModalConflictFn } from 'app/components/appModals/components/keyCombinationRecorderModal/keyCombinationRecorderModal.types';
import { KeyboardKeyCombinations } from 'components/keyboard';
import { useCommands } from 'utils/commands';
import {
  createPrettyKeyCombination,
  KeyboardKeyCombination,
} from 'utils/keyboard';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { KeyboardKeybindingsTableRowContext } from '../../utils/keyboardKeybindingsTableRowContext';

/** Represents the component for editing the keybindings of a command. */
export const EditKeybindingsAction = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const { setKeybindings, enrichedCommands } = useCommands();
  const {
    enrichedCommand: {
      command: { label, name },
      isKeyboardRebindable,
    },
  } = useContext(KeyboardKeybindingsTableRowContext);
  const isNonRebindable = !isKeyboardRebindable;

  const description = (
    <>
      <Text.Paragraph variant="4">
        Record and assign new keybinding for <strong>{label}</strong> command.
      </Text.Paragraph>

      <Text.Paragraph variant="4" subdued>
        Note that recording of certain key combinations could conflict with the
        browser and/or operating system native keyboard shortcuts, e.g.{' '}
        <KeyboardKeyCombinations
          keyCombinations={[
            { binding: [['$metaOrCtrl'], 'Digit1'] },
            { binding: [['$metaOrCtrl'], 'KeyT'] },
            { binding: [['$metaOrCtrl'], 'KeyW'] },
          ]}
        />
      </Text.Paragraph>
    </>
  );

  const handleClick = async () => {
    const conflictFn: KeyCombinationRecorderModalConflictFn = (
      keyCombination,
    ) => {
      const conflict = enrichedCommands.find(({ command: { keyboard } }) => {
        const keybindings = keyboard?.keybindings;

        if (keybindings) {
          return compareKeyCombinations(keyCombination, keybindings);
        }
      });

      if (conflict) {
        return {
          message: (
            <Banner
              variant="warning"
              description={
                <Text.Paragraph variant="4">
                  This key combination is already assigned to{' '}
                  <strong>{conflict.command.label}</strong> command
                </Text.Paragraph>
              }
            />
          ),
        };
      }

      return false;
    };

    const { keyCombination, isConfirmed } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:keyCombinationRecorder',
          arg: {
            title: 'Assign new keybinding',
            description,
            confirm: {
              label: 'Assign',
            },
            conflictFn,
          },
        },
      }),
    );

    if (isConfirmed && keyCombination) {
      setKeybindings(name, [keyCombination]);
    }
  };

  return (
    <IconButton
      icon="pen"
      variant="tertiary"
      disabled={isNonRebindable}
      onClick={() => handleClick()}
    />
  );
};

/**
 * Checks whether the given source key combination is already present in the
 * given target list of key combinations.
 *
 * @param a The source key combination.
 * @param b The target list of key combinations.
 */
function compareKeyCombinations(
  a: KeyboardKeyCombination,
  b: readonly KeyboardKeyCombination[],
): boolean {
  const aString = createPrettyKeyCombination(a, {
    preferBinding: true,
  })?.toString();

  return b.some((keyCombination) => {
    const bString = createPrettyKeyCombination(keyCombination, {
      preferBinding: true,
    })?.toString();

    return aString === bString;
  });
}
